import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Paper, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import {outlinedButtonSx, primaryButtonSx} from '../utils/button_styles';
import TextInput from '../components/inputs/TextInput';
import PhoneNumberInput from '../components/inputs/PhoneNumberInput';
import EmailInput from '../components/inputs/EmailInput';
import CheckboxInput from '../components/inputs/CheckboxInput';
import AutocompleteInput from '../components/inputs/AutocompleteInput';
import RadioInput from '../components/inputs/RadioInput';

const RenderAllFormComponent = (props) => {
  const {
    pageTitle,
    pageQuestions,
    answersByQuestion,
    onTextChange,
    onRadioSelect,
    onCheckboxToggle,
    onOtherTextChange,
    onSubmit,
    onPrevious,
    isSubmitting,
    isNextDisabled,
    hasPrevious,
    isFinalSubmit,
    fieldLayout,
    compactFields,
  } = props;

  return (
    <Paper
      sx={{
        padding: {xs: '16px', sm: '24px'},
        borderRadius: '20px',
        boxShadow: 'none',
      }}
    >
      <Stack spacing={{xs: 2, sm: 3}}>
        {
          pageTitle && (
            <Typography
              sx={{
                fontWeight: 300,
                fontSize: {xs: '24px', sm: '28px', md: '32px'},
                color: '#2D2925',
                fontFamily: 'Spectral Light',
                lineHeight: '120%',
                letterSpacing: '-2%',
              }}
            >
              {pageTitle}
            </Typography>
          )
        }
        <Stack
          sx={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            columnGap: {xs: 2, sm: 3},
            rowGap: {xs: 2, sm: 3},
          }}
        >
          {
            pageQuestions.map((question, index) => {
              const answer = answersByQuestion[question.questionId] || {};
              const rawValue = answer?.questionData?.value;
              const optionValues = (question.options || []).map((option) => option.value);

              const isKnownOption = optionValues.includes(rawValue);
              const radioValue = isKnownOption ? rawValue : (rawValue ? 'other' : '');
              const radioOtherText = isKnownOption || rawValue === 'other' ? '' : String(rawValue || '');

              const checkboxValues = rawValue?.selected || rawValue || [];
              const checkboxOtherText = rawValue?.otherText || '';

              const widthOverride = fieldLayout?.[question.questionId];
              const isCompact = compactFields?.includes(question.questionId);
              return (
                <Box key={question.questionId || `question-${index}`} sx={{width: widthOverride || '100%'}}>
                  <Typography
                    sx={{
                      fontWeight: isCompact ? 400 : 300,
                      fontSize: isCompact ? '16px' : {xs: '20px', sm: '24px'},
                      color: '#191716',
                      lineHeight: '120%',
                      letterSpacing: '-2%',
                      marginBottom: {xs: 1, sm: 1.5},
                    }}
                  >
                    {question?.questionData?.label}
                  </Typography>
                  {
                    question.fieldType === 'text' && (
                      <TextInput
                        questionData={question.questionData}
                        value={rawValue || ''}
                        onChange={(value) => onTextChange(question.questionId, value)}
                        multiline={question.questionData?.multiline}
                        rows={question.questionData?.rows}
                        minRows={question.questionData?.minRows}
                        maxRows={question.questionData?.maxRows}
                      />
                    )
                  }
                  {
                    question.fieldType === 'phoneNumber' && (
                      <PhoneNumberInput
                        questionData={question.questionData}
                        value={rawValue || ''}
                        onChange={(value) => onTextChange(question.questionId, value)}
                      />
                    )
                  }
                  {
                    question.fieldType === 'email' && (
                      <EmailInput
                        questionData={question.questionData}
                        value={rawValue || ''}
                        onChange={(value) => onTextChange(question.questionId, value)}
                      />
                    )
                  }
                  {
                    question.fieldType === 'radio' && (
                      <RadioInput
                        questionData={question.questionData}
                        options={question.options}
                        value={radioValue}
                        otherText={radioOtherText}
                        onSelect={(option) => onRadioSelect(question.questionId, option)}
                        onOtherTextChange={(value) => onOtherTextChange(question.questionId, value)}
                      />
                    )
                  }
                  {
                    question.fieldType === 'checkbox' && (
                      <CheckboxInput
                        questionData={question.questionData}
                        options={question.options}
                        values={checkboxValues}
                        otherText={checkboxOtherText}
                        onToggle={(optionValue, checked) =>
                          onCheckboxToggle(question.questionId, optionValue, checked)}
                        onOtherTextChange={(value) => onOtherTextChange(question.questionId, value)}
                      />
                    )
                  }
                  {
                    question.fieldType === 'dropdown' && (
                      <AutocompleteInput
                        questionData={question.questionData}
                        value={rawValue || ''}
                        onChange={(value) => onTextChange(question.questionId, value)}
                        options={question.options}
                      />
                    )
                  }
                </Box>
              );
            })}
        </Stack>
        <Stack>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              alignItems: 'center',
              gap: {xs: '12px', sm: '0px'},
              flexWrap: 'nowrap',
            }}
          >
            {
              hasPrevious && (
                <Button
                  variant='outlined'
                  onClick={onPrevious}
                  sx={{
                    ...outlinedButtonSx,
                    width: '140px',
                    minWidth: '140px',
                  }}
                >
                  <Translate expression='form.FormStep.Previous' />
                </Button>
              )
            }
            <Button
              variant='contained'
              onClick={onSubmit}
              disabled={isSubmitting || isNextDisabled}
              sx={{
                ...primaryButtonSx,
                ...(isSubmitting || isNextDisabled ? {
                  backgroundColor: '#DED8D7',
                  color: '#2D2925B8',
                } : {}),
                width: '140px',
                minWidth: '140px',
                marginLeft: 'auto',
              }}
            >
              {
                isFinalSubmit ?
                <Translate expression='form.FormStep.Submit' /> :
                <Translate expression='form.FormStep.Next' />
              }
            </Button>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
};

RenderAllFormComponent.propTypes = {
  pageTitle: PropTypes.string,
  pageQuestions: PropTypes.arrayOf(PropTypes.shape({
    questionId: PropTypes.string,
    fieldType: PropTypes.string,
    questionData: PropTypes.object,
    options: PropTypes.array,
  })).isRequired,
  answersByQuestion: PropTypes.object.isRequired,
  onTextChange: PropTypes.func.isRequired,
  onRadioSelect: PropTypes.func.isRequired,
  onCheckboxToggle: PropTypes.func.isRequired,
  onOtherTextChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  isNextDisabled: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  isFinalSubmit: PropTypes.bool,
  fieldLayout: PropTypes.object,
  compactFields: PropTypes.arrayOf(PropTypes.string),
};

export default React.memo(RenderAllFormComponent);
